import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  animated,
  easings,
  useReducedMotion,
  useSpring,
  useSpringValue,
} from '@react-spring/web';
import type { SpringValue } from '@react-spring/web';
import type { Block, Inline } from '@contentful/rich-text-types';
import { targetMap } from 'src/lib/constants';
import { makeImageQueryString } from 'src/lib/utils';
import type { Button2Entry } from 'src/types/contentful';
import type { Sys } from 'src/types/contentful-sdk-compat';

import Box from 'src/components-v2/Layout/Box/Box';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import { Typography } from 'src/components-v2/DataDisplay/Typography';
import BtnPrimary from 'src/components-v2/Inputs/Button/BtnPrimary';
import Image from 'src/components-v2/Media/Image/Image';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { Disclaimer, ToggleInput } from '../Calculator/Calculator.styled';
import {
  MetricToggleLabel,
  MetricToggleTrack,
  ResultCard,
  ToggleKnob,
} from './LendScoreCalculator.styled';
import type { Projection } from './lendScoreCalculations';

// U+2212, not a hyphen, so the sign matches the width and height of the plus
// on the approval rate headline.
const MINUS = '\u2212';

// What the headlines show: the size of the change, rounded the way it is
// displayed. Deciding what counts as a change from these, rather than from the
// raw figures, keeps the card from treating a change it shows as 0 as a loss.
const roundedPoints = (points: number) => {
  return Math.round(Math.abs(points) * 10) / 10;
};

const roundedPercent = (reduction: number) => {
  return Math.round(Math.abs(reduction) * 100);
};

// The sign follows the rounded figure, so a change too small to show reads as
// 0.0 rather than as -0.0.
const formatPoints = (points: number) => {
  const rounded = roundedPoints(points);
  const sign = rounded === 0 ? '' : points > 0 ? '+' : MINUS;

  return `${sign}${rounded.toFixed(1)} pts`;
};

// A reduction is the loss rate falling, so it is the one that gets the minus.
const formatReduction = (reduction: number) => {
  const percent = roundedPercent(reduction);
  const sign = percent === 0 ? '' : reduction > 0 ? MINUS : '+';

  return `${sign}${percent}%`;
};

// The ends of the approval rate slider have no result of their own. Approving
// nobody leaves no losses to compare, and approving everybody leaves LendScore
// no one to turn away, since it can only reorder who is approved. The card
// explains that rather than printing a gain of zero, or a gain over nothing.
//
// Anywhere else LendScore is a result, even where it comes out behind the
// score a lender uses today: the headline then reads as a loss, and is what
// the ROI data says.
const isAtEitherEnd = ({ approvalRate }: Projection) => {
  return approvalRate.without <= 0 || approvalRate.without >= 100;
};

// What the results read while there is no projection. They are hidden behind
// the message, but still laid out, so that the card is no shorter than it is
// with figures in them.
const NO_PROJECTION: Projection = {
  approvalRate: { increase: 0, with: 0, without: 0 },
  lossRate: { reduction: 0, with: 0, without: 0 },
};

const formatRate = (rate: number) => {
  return `${rate.toFixed(1)}%`;
};

// Approval rate moves in percentage points, loss rate in percent, so each
// metric carries its own headline rather than sharing a unit.
const METRICS = [
  {
    headline: ({ approvalRate }: Projection) => {
      return formatPoints(approvalRate.increase);
    },
    heading: 'Your approval rate growth',
    id: 'lend-score-card-approval-rate',
    key: 'approvalRate',
    label: 'Approval rate',
    rate: ({ approvalRate }: Projection) => {
      return formatRate(approvalRate.with);
    },
    rowLabel: 'Approval rate with LendScore',
  },
  {
    headline: ({ lossRate }: Projection) => {
      return formatReduction(lossRate.reduction);
    },
    heading: 'Your delinquency rate reduction',
    id: 'lend-score-card-delinquency-rate',
    key: 'delinquencyRate',
    label: 'Delinquency rate',
    rate: ({ lossRate }: Projection) => {
      return formatRate(lossRate.with);
    },
    rowLabel: 'Loss rate with LendScore',
  },
] as const;

// Exported so the calculator's active rate is typed by the same union. The
// toggle and the rate sliders are two views of one selection, so if either
// list's keys are renamed the other stops compiling.
export type MetricKey = (typeof METRICS)[number]['key'];

type Metric = (typeof METRICS)[number];

// The knob sits on the second metric at translateX(100%), so the key that
// belongs to each end of the track is worth naming.
const [FIRST_METRIC, SECOND_METRIC] = METRICS.map((metric) => {
  return metric.key;
}) as [MetricKey, MetricKey];

// Pointer travel, in px, that separates a click from a drag. Below this the
// knob stays put and the label's own click handling takes over.
const DRAG_SLOP = 3;

type DragState = {
  hasMoved: boolean;
  pointerId: number;
  // The half the gesture began over, so one that ends up going nowhere can
  // still be treated as a press on that half.
  pressedKey: MetricKey;
  // How far along the track the knob currently sits, 0 to 100.
  progress: number;
  startProgress: number;
  startX: number;
  // How far the knob has to move to cross the track, which is its own width.
  travel: number;
};

type MetricSwapProps = {
  activeKey: MetricKey;
  children: (metric: Metric) => React.ReactNode;
};

/**
 * Stacks every metric in a single grid cell and hides the inactive ones, so
 * the block always reserves the height of its tallest option. In the narrow
 * desktop column the longer heading wraps onto a second line, and without
 * this the rest of the card jumps down when the toggle changes.
 */
const MetricSwap: React.FC<MetricSwapProps> = ({ activeKey, children }) => {
  return (
    <Box sx={{ display: 'grid' }}>
      {METRICS.map((metric) => {
        const isActive = metric.key === activeKey;

        return (
          <Box
            key={metric.key}
            aria-hidden={!isActive}
            sx={{
              gridArea: '1 / 1',
              visibility: isActive ? 'visible' : 'hidden',
            }}
          >
            {children(metric)}
          </Box>
        );
      })}
    </Box>
  );
};

// What the button says and where it goes when no Button entry is linked.
const DEFAULT_CTA_HREF = '/contact/';
const DEFAULT_CTA_TEXT = 'Talk with our team';

// What the card says instead of the results, and what the button offers.
const ERROR_MESSAGE =
  'Hmm, that didn’t quite work. Adjust the sliders to run a new calculation.';
const ERROR_CTA_TEXT = 'Reach out for help';

// The illustration the mortgage calculator's error state uses, at the size
// and corner offset this card's design gives it.
const SPOT_IMAGE_URL =
  'https://images.ctfassets.net/ss5kfr270og3/k1m6JmvViVpIdhxE34EPS/777c7daa0c95b8520cddc18e17a8e78b/Spot-Magnify-001.png';
const SPOT_IMAGE_WIDTH = 245;
const SPOT_IMAGE_HEIGHT = 256;
const SPOT_IMAGE_QUALITY = 100;

// The results slide out and the message slides in after them, which is the
// mortgage calculator's error state. Each half takes this long.
const ERROR_DURATION = 280;

const AnimatedStack = animated(Stack);

type SlideSpring = { opacity: SpringValue<number>; x: SpringValue<number> };

// Fading and sliding in one go. Fully faded out also means hidden, so that what
// is no longer showing, such as the toggle, can't be tabbed to either.
const slideStyle = (spring: SlideSpring) => {
  return {
    opacity: spring.opacity,
    transform: spring.x.to((x) => {
      return `translateX(${x}px)`;
    }),
    visibility: spring.opacity.to((opacity): 'hidden' | 'visible' => {
      return opacity === 0 ? 'hidden' : 'visible';
    }),
  } as unknown as React.CSSProperties;
};

type LendScoreCardProps = {
  fields?: {
    cardCtaButton?: Button2Entry;
    cardDisclaimer?: Block | Inline;
  };
  // The selection lives in the calculator, because the rate sliders on the
  // other side of it are the same choice shown a different way.
  metricKey: MetricKey;
  onMetricChange: (key: MetricKey) => void;
  // Both metrics are laid out at once so the card keeps its height, which
  // means both are needed whichever one is showing. There is none when the ROI
  // data has nothing for the selection, which the card explains in the same
  // way as a result it has nothing to show for.
  projection?: Projection;
  sys?: Sys;
};

export const LendScoreCard: React.FC<LendScoreCardProps> = ({
  fields,
  metricKey,
  onMetricChange,
  projection,
  sys,
}) => {
  const prefersReducedMotion = useReducedMotion();

  // The results give way to the message, which comes in once they have gone,
  // and the illustration arrives with it. Putting the results back is not
  // delayed, so the card never sits empty while the sliders are being moved.
  // People who prefer reduced motion get no animation: the hook above is also
  // what tells react-spring to skip every animation, delays included.
  const hasError = !projection || isAtEitherEnd(projection);
  const enterDelay = hasError ? ERROR_DURATION : 0;
  const resultsSpring = useSpring({
    config: { duration: ERROR_DURATION },
    opacity: hasError ? 0 : 1,
    x: hasError ? 16 : 0,
  });
  const messageSpring = useSpring({
    config: { duration: ERROR_DURATION },
    delay: enterDelay,
    opacity: hasError ? 1 : 0,
    x: hasError ? 0 : -24,
  });
  const spotImageSpring = useSpring({
    config: { duration: ERROR_DURATION },
    delay: enterDelay,
    opacity: hasError ? 1 : 0,
    x: hasError ? 0 : 24,
  });

  // An editor can link a Button entry to point the button somewhere else, such
  // as `#contact-form` to scroll to the contact form on the same page. Without
  // one, or while the linked entry is unpublished, the button keeps going to
  // the contact page.
  const ctaButton = fields?.cardCtaButton?.fields;
  const opensInNewTab =
    !!ctaButton?.target && targetMap[ctaButton.target] === '_blank';

  // The same spring the ACH calculator's knob rides, so both toggles overshoot
  // and settle identically. It carries how far along the track the knob is
  // rather than the transform itself: react-spring animates a string by
  // interpolating between a fixed pair of them, which a drag can't drive.
  const knobProgress = useSpringValue(0, {
    config: {
      duration: 600,
      easing: easings.easeInOutBack,
    },
  });
  const knobTransform = useMemo(() => {
    return knobProgress.to((progress) => {
      return `translateX(${progress}%)`;
    });
  }, [knobProgress]);

  const springTo = (key: MetricKey) => {
    knobProgress.start(key === SECOND_METRIC ? 100 : 0, {
      // The ACH knob springs regardless; this one skips straight to the end
      // for reduced-motion users, which is what its CSS transition did.
      immediate: prefersReducedMotion === true,
    });
  };

  // The knob can also be dragged. It tracks the pointer for the whole gesture,
  // and only on release does it spring to whichever side it is more than
  // halfway towards, committing that metric.
  const knobRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragEndTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // A drag that ends over a label is followed by a click on it, which would
  // select that metric and undo whatever the drag decided. The labels are held
  // inert until the click has come and gone, so it lands on the track instead.
  // The same 100ms guard the ACH calculator uses for its drag.
  const endDrag = () => {
    clearTimeout(dragEndTimerRef.current);
    dragEndTimerRef.current = setTimeout(() => {
      setIsDragging(false);
    }, 100);
  };

  useEffect(() => {
    return () => {
      clearTimeout(dragEndTimerRef.current);
    };
  }, []);

  // Brings the knob to the selection however it changed, which covers a rate
  // slider being picked up on the other side of the calculator as well as the
  // toggle's own labels. A drag owns the knob until it is let go, so it is
  // left alone while one is in progress.
  useEffect(() => {
    if (dragRef.current) return;

    knobProgress.start(metricKey === SECOND_METRIC ? 100 : 0, {
      immediate: prefersReducedMotion === true,
    });
  }, [knobProgress, metricKey, prefersReducedMotion]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Left button only, so a right-click doesn't start a gesture that never
    // gets a matching pointerup.
    if (event.button !== 0) return;

    const travel = knobRef.current?.offsetWidth ?? 0;
    if (travel === 0) return;

    // A previous gesture may still be holding the labels inert. Starting a new
    // one cancels that, or the timer would fire mid-drag and let the release
    // click land on a label and override what this drag chose.
    clearTimeout(dragEndTimerRef.current);

    // Where the knob actually is, not where the selection says it should be,
    // so grabbing one that is still springing picks it up mid-flight instead
    // of making it jump.
    const startProgress = knobProgress.get();

    const track = event.currentTarget.getBoundingClientRect();

    dragRef.current = {
      hasMoved: false,
      pointerId: event.pointerId,
      pressedKey:
        event.clientX < track.left + track.width / 2
          ? FIRST_METRIC
          : SECOND_METRIC,
      progress: startProgress,
      startProgress,
      startX: event.clientX,
      travel,
    };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    // Until capture is taken, a press released off the pill never sends its
    // pointerup here, and a mouse keeps the same pointerId. Without this, the
    // next hover would pick that gesture back up with no button held.
    if ((event.buttons & 1) === 0) {
      handlePointerUp(event);
      return;
    }

    const offset = event.clientX - drag.startX;

    if (!drag.hasMoved) {
      if (Math.abs(offset) < DRAG_SLOP) return;
      drag.hasMoved = true;
      setIsDragging(true);
      // Keeps the moves coming once the pointer leaves the pill. Capture is
      // taken here rather than on pointerdown because it also retargets the
      // click that follows onto the capturing element, which stopped a plain
      // click from ever reaching its label.
      event.currentTarget.setPointerCapture(event.pointerId);
    }

    drag.progress = Math.min(
      100,
      Math.max(0, drag.startProgress + (offset / drag.travel) * 100),
    );

    // No easing while the knob is under the pointer, so it doesn't lag behind.
    knobProgress.set(drag.progress);
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    dragRef.current = null;

    if (!drag.hasMoved) {
      setIsDragging(false);
      return;
    }

    endDrag();

    // Whichever side the knob is more than halfway towards wins.
    const nextKey = drag.progress > 50 ? SECOND_METRIC : FIRST_METRIC;

    // A gesture that lands back on the metric it started from moved the knob
    // nowhere, so read it as a press on the half it began over. Touch taps
    // routinely wander past DRAG_SLOP without ever reaching halfway, and the
    // labels are inert by now, so otherwise that tap would select nothing.
    const settledKey = nextKey === metricKey ? drag.pressedKey : nextKey;

    if (settledKey === metricKey) {
      // A drag that stopped short changes nothing, so no selection update is
      // coming and the knob has to be put back from here.
      springTo(settledKey);
      return;
    }

    onMetricChange(settledKey);
  };

  return (
    <ResultCard>
      <Stack gap={5} sx={{ height: '100%' }}>
        {/* The results stay in the layout while the message is up, so the card
            keeps its height, and the message is laid over them. */}
        <Box sx={{ position: 'relative' }}>
          <AnimatedStack
            gap={5}
            style={slideStyle(resultsSpring)}
            aria-hidden={hasError}
          >
            <MetricToggleTrack
              direction='row'
              gap={1}
              isDragging={isDragging}
              onPointerCancel={handlePointerUp}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
            >
              <ToggleKnob ref={knobRef} style={{ transform: knobTransform }} />
              {METRICS.map((option, index) => {
                const isChecked = option.key === metricKey;

                return (
                  <MetricToggleLabel
                    key={option.key}
                    htmlFor={option.id}
                    isChecked={isChecked}
                    // The labels overlap the track, so the second one is nudged
                    // past the first rather than laid out after it.
                    sx={index === 1 ? { left: 'calc(50% - 3px)' } : undefined}
                  >
                    <ToggleInput
                      type='radio'
                      id={option.id}
                      name='lend-score-card-metric'
                      value={option.key}
                      checked={isChecked}
                      onChange={() => {
                        onMetricChange(option.key);
                      }}
                    />
                    {option.label}
                  </MetricToggleLabel>
                );
              })}
            </MetricToggleTrack>

            <MetricSwap activeKey={metricKey}>
              {(metric) => {
                return (
                  <Stack>
                    {/* Subhead SemiBold is the design system's 24px style, so
                        the heading takes it by name rather than overriding a
                        web 2.0 variant's size the way the ACH card does. */}
                    <Typography
                      variant='Subhead SemiBold'
                      theme='web 3.0'
                      component='span'
                    >
                      {metric.heading}
                    </Typography>
                    <Typography
                      variant='h2'
                      component='span'
                      sx={{
                        fontSize: { xs: '4.0rem', sm: '6.0rem' },
                        mb: '-15px',
                      }}
                    >
                      {metric.headline(projection ?? NO_PROJECTION)}
                    </Typography>
                  </Stack>
                );
              }}
            </MetricSwap>

            <MetricSwap activeKey={metricKey}>
              {(metric) => {
                return (
                  /* P2 SemiBold is the design system's 18px semibold style, so
                     the row no longer needs a fontWeight override. */
                  <Stack direction='row' gap={1} justifyContent='space-between'>
                    <Typography
                      variant='P2 SemiBold'
                      theme='web 3.0'
                      component='span'
                    >
                      {metric.rowLabel}
                    </Typography>
                    <Typography
                      variant='P2 SemiBold'
                      theme='web 3.0'
                      component='span'
                    >
                      {metric.rate(projection ?? NO_PROJECTION)}
                    </Typography>
                  </Stack>
                );
              }}
            </MetricSwap>

            <hr />
          </AnimatedStack>

          <AnimatedStack
            component='div'
            style={slideStyle(messageSpring)}
            sx={{
              display: 'flex',
              inset: 0,
              // The overlay is a column, so this is what centres the message
              // between the top of the card and the button, rather than
              // leaving it where the results' toggle was.
              justifyContent: 'center',
              pointerEvents: hasError ? 'auto' : 'none',
              position: 'absolute',
              zIndex: 1,
            }}
            aria-hidden={!hasError}
            aria-live='polite'
            role='status'
          >
            <Typography
              variant='h4'
              component='p'
              sx={{ fontSize: { xs: '2.4rem', sm: '2.7rem' } }}
            >
              {ERROR_MESSAGE}
            </Typography>
          </AnimatedStack>
        </Box>

        <BtnPrimary
          href={ctaButton?.url || DEFAULT_CTA_HREF}
          // Every target the CMS offers other than a new tab is the same tab,
          // which is what an anchor does without a target.
          target={opensInNewTab ? ('_blank' as const) : undefined}
          trackingId={ctaButton?.trackingId}
          sys={fields?.cardCtaButton?.sys}
          sx={(theme) => {
            return {
              // The shared Card paints every descendant p and span with its
              // own text colour, and the button label is a span, so it has to
              // be handed back the button's own colour or it turns black on a
              // black fill.
              '& .txt': {
                color: `${theme.palette['web3/component/button/primary/resting-text']} !important`,
              },
              '&:hover .txt, &:active .txt': {
                color: `${theme.palette['web3/component/button/primary/interacted-text']} !important`,
              },
            };
          }}
        >
          {hasError ? ERROR_CTA_TEXT : ctaButton?.text || DEFAULT_CTA_TEXT}
        </BtnPrimary>

        <AnimatedStack style={slideStyle(resultsSpring)} aria-hidden={hasError}>
          <Disclaimer>
            <RichTextSwitch
              sys={sys}
              content={fields?.cardDisclaimer}
              nodes={{
                p: {
                  variant: 'p',
                  component: 'p',
                  fieldId: 'cardDisclaimer',
                },
              }}
            />
          </Disclaimer>
        </AnimatedStack>
      </Stack>
      <AnimatedStack
        component='div'
        style={slideStyle(spotImageSpring)}
        sx={{
          pointerEvents: 'none',
          position: 'absolute',
          right: '-17px',
          top: '-26px',
        }}
        aria-hidden
        role='presentation'
      >
        <Image
          src={`${SPOT_IMAGE_URL}?fm=webp&q=${SPOT_IMAGE_QUALITY}&w=${
            SPOT_IMAGE_WIDTH * 2
          }`}
          srcset={makeImageQueryString({
            src: SPOT_IMAGE_URL,
            width: SPOT_IMAGE_WIDTH,
            quality: SPOT_IMAGE_QUALITY,
          })}
          alt=''
          width={SPOT_IMAGE_WIDTH}
          height={SPOT_IMAGE_HEIGHT}
          isLazy={false}
          sx={{ display: 'block' }}
        />
      </AnimatedStack>
    </ResultCard>
  );
};
